import { animated, useSpring } from 'react-spring'

export interface SpinnerProps {
  thickness?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
  /** ie: 1rem */
  size?: string
  /** style object passed to svg */
  style?: React.CSSProperties
}

interface SvgCircleProps {
  cx: number
  cy: number
  fill: string
  r: number
  strokeLinecap: 'round'
  strokeWidth: number
}

/**
 * Generic spinning loader component
 *
 * Generally used in:
 *  - Buttons
 *
 */
export const Spinner = ({
  thickness = 4,
  size = '1.75rem',
  style,
}: SpinnerProps) => {
  const springValues = useSpring({
    from: { x: 0 },
    to: { x: 1 },
    config: { duration: 2000 },
    loop: true,
  })

  const rotate = springValues.x
    .to({ range: [0, 1], output: [0, 360] })
    .to((x: number) => `rotate(${Math.ceil(x)}deg)`)

  const strokeDasharray = 80
  const strokeDashoffset = springValues.x.to(
    [0, 1],
    [strokeDasharray, -strokeDasharray]
  )

  const circleProps: SvgCircleProps = {
    cx: 16,
    cy: 16,
    fill: 'none',
    r: 15 - thickness / 2,
    strokeLinecap: 'round',
    strokeWidth: thickness,
  }

  return (
    <svg
      height={size}
      role="presentation"
      style={style}
      viewBox="0 0 32 32"
      width={size}
    >
      <circle {...circleProps} stroke="rgba(255,255,255,0.75)" />
      <animated.circle
        {...circleProps}
        stroke="currentcolor"
        strokeDasharray={strokeDasharray}
        strokeDashoffset={strokeDashoffset}
        style={{
          transform: rotate,
          transformOrigin: 'center',
        }}
      />
    </svg>
  )
}
